/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	type ResourceStoreContext,
	type RouterDataContext,
	type RouteResource,
} from '@atlassian/react-resource-router';
import { HelpHelpCenterAri } from '@atlassian/ari/help/help-center';

export type WidgetAnonymousAccessResponse = {
	canWidgetBeAccessedByAnonymousUsers: boolean;
};

type WidgetAccessApiResponse = {
	hasAccess: boolean;
	anonAuthToken: string | null;
};

/**
 * Resource to check if anonymous access is enabled for the CSM widget.
 * Makes a request to the widgets/access endpoint without authentication to determine
 * if anonymous users can access the widget (public access).
 *
 * Requires `helpCenterId` in route params and `activationId` in the resource store context.
 */
export const widgetAnonymousAccessResource: RouteResource<WidgetAnonymousAccessResponse> =
	createResource<WidgetAnonymousAccessResponse>({
		type: 'CSM_WIDGET_ANONYMOUS_ACCESS',
		getKey: () => 'widget-anonymous-access',
		getData: async ({ match }: RouterDataContext, { tenantContext }: ResourceStoreContext) => {
			const helpCenterId = match.params.helpCenterId;
			const activationId = tenantContext.activationId;

			if (!helpCenterId) {
				throw new Error('Help center ID is not set');
			}

			const helpCenterAri = HelpHelpCenterAri.create({
				activationId,
				helpCenterId,
			}).toString();
			try {
				const data = await performGetRequest<WidgetAccessApiResponse>(
					`/gateway/api/assist/csm/v2/widgets/access?helpCenterAri=${helpCenterAri}`,
					{
						headers: {
							'x-product': 'csm',
							'x-experience-id': 'csm-ai',
						},
						// Use 'omit' to make a truly anonymous request without sending cookies
						credentials: 'omit',
					},
				);

				return {
					canWidgetBeAccessedByAnonymousUsers: data.hasAccess,
				};
			} catch (error) {
				fireErrorAnalytics({
					meta: {
						id: 'widgetAnonymousAccessResource',
						packageName: 'jiraRouterResourcesWidgetAnonymousAccess',
						teamName: 'csm-ai',
					},
					error: error instanceof Error ? error : new Error('Failed to check anonymous access'),
				});

				throw error;
			}
		},
		isBrowserOnly: true,
	});
